import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Rnd } from "react-rnd";
import {
  primary_00,
  white,
  primary,
  grey,
  primayColor,
} from "../styled/variable";
import { debounce } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Flex, Text } from "@chakra-ui/react";
import { Input } from "@chakra-ui/react";

const StyledTrack = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  display: flex;
  align-items: center;

  .track {
    background-color: rgba(0, 0, 0, 0.24);
    height: 50%;
    width: 100%;
    border-radius: 5px;
  }
`;

const StyledGrabber = styled(Rnd)`
  height: 100% !important;
  border-radius: 8px;
  display: flex !important;
  align-items: center;

  .bar {
    height: 50%;
    width: 100%;
    background-color: ${primary_00};
  }

  .left,
  .right {
    border-radius: 50%;
    background-color: ${white};
    height: 14px !important;
    width: 14px !important;
    transform: translateY(-50%);
    top: 50% !important;
  }
`;

const StyledLabel = styled.label`
  color: ${white};
  font-size: 10px;
  margin: 0;
`;

const StyledInput = styled("input")`
  height: 30px;
  width: 60px;
  background-color: ${primayColor};
  border: 0px;
  color: ${grey};
  border-radius: 8px;
  padding-left: 1rem;
  font-size: 1rem;
  letter-spacing: 0.08px;
  line-height: 28px;

  &::placeholder {
    font-size: 1rem;
    letter-spacing: 0.08px;
    line-height: 28px;
  }

  &:-ms-input-placeholder {
    background-color: ${primayColor} !important;
    color: ${grey};
    font-size: 1rem;
    letter-spacing: 0.08px;
    line-height: 28px;
  }

  &::-ms-input-placeholder {
    background-color: ${primayColor} !important;
    color: ${grey};
    font-size: 1rem;
    letter-spacing: 0.08px;
    line-height: 28px;
  }

  .input {
    background-color: ${primayColor} !important;
    color: ${grey} !important;
    outline: none;
  }

  :focus {
    color: ${grey};
    background-color: ${primayColor} !important;
  }

  :hover {
    border: 1px ${primary} solid !important;
  }

  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }
`;

const TldrRndSlider = ({
  showInputBoxes,
  start,
  end,
  minValue,
  maxValue,
  totalValue,
  onChange,
}) => {
  if (!maxValue) {
    maxValue = totalValue;
  }

  const debouncedOnChange = useCallback(
    debounce(async (start, end) => {
      onChange(start, end);
    }, 1000),
    [onChange]
  );

  const SliderSchema = useMemo(
    () =>
      Yup.object().shape({
        start: Yup.number()
          .required("Start value is required")
          .min(0, "Should be greater than or equal to 0")
          .max(totalValue),
        end: Yup.number()
          .required("End value is required")
          .min(0, "Should be greater than or equal to 0"),
      }),
    [totalValue]
  );

  const {
    handleChange,
    errors,
    handleBlur,
    values,
    setValues,
    validateForm,
    submitForm,
  } = useFormik({
    initialValues: {
      start: start,
      end: Math.min(start + maxValue, end),
    },
    enableReinitialize: true,
    validationSchema: SliderSchema,
    validate: (values) => {
      const errors = {};
      if (
        Number((values.end - values.start).toFixed(1)) <
        Number(minValue.toFixed(1))
      ) {
        errors.all = `Min required duration is ${minValue}s`;
      }
      if (values.start > values.end) {
        errors.all = "Start value should be less than end value";
      }
      if (
        Number((values.end - values.start).toFixed(1)) >
        Number(maxValue.toFixed(1))
      ) {
        errors.end = `End duration should be less than ${
          values.start + maxValue
        }`;
      }
      return errors;
    },
    onSubmit: (res) => {
      const [x, width] = getXAndWidth(res.start, res.end, totalValue);
      setSliderPositions({
        ...sliderState,
        sliderX: x,
        sliderWidth: width,
      });
      debouncedOnChange(res.start, res.end);
    },
  });

  const [sliderState, setSliderPositions] = useState({
    sliderX: 0,
    sliderWidth: "100%",
    sliderMinimumWidth: "10%",
    sliderMaximumWidth: "100%",
    startValue: start,
    endValue: end,
    errors: {},
  });
  const {
    sliderX,
    sliderWidth,
    sliderMinimumWidth,
    sliderMaximumWidth,
    startValue,
    endValue,
  } = sliderState;

  const sliderTrackRef = useRef(null);

  const getXAndWidth = (startValue, endValue, totalValue) => {
    startValue = Math.max(startValue, 0);
    endValue = Math.min(endValue, totalValue);
    let xValue = 0,
      widthValue = 0;
    if (sliderTrackRef.current) {
      // X value calculations
      if (!startValue || !totalValue) {
        xValue = 0;
      } else {
        xValue = (sliderTrackRef.current.offsetWidth * startValue) / totalValue;
      }

      // Width Calculations
      if (!endValue) {
        widthValue = !totalValue ? 0 : sliderTrackRef.current.offsetWidth;
      } else {
        widthValue =
          ((endValue - startValue) / totalValue) *
          sliderTrackRef.current.offsetWidth;
      }
    }

    return [xValue, widthValue];
  };

  const onDrag = async (e, { x, node }, commit = false) => {
    setSliderPositions({
      ...sliderState,
      sliderX: x,
      sliderWidth: node.offsetWidth,
    });
    const start = Number(
      ((x / sliderTrackRef.current.offsetWidth) * totalValue).toFixed(1)
    );
    const end = Number(
      (
        start +
        (node.offsetWidth / sliderTrackRef.current.offsetWidth) * totalValue
      ).toFixed(1)
    );
    await setValues({
      start,
      end,
    });
    if (commit) {
      await submitForm();
    }
  };

  const onResize = async (e, direction, ref, delta, pos, commit = false) => {
    setSliderPositions({
      ...sliderState,
      sliderX: pos.x,
      sliderWidth: ref.offsetWidth,
    });
    const start = Number(
      ((pos.x / sliderTrackRef.current.offsetWidth) * totalValue).toFixed(1)
    );
    const end = Number(
      (
        start +
        (ref.offsetWidth / sliderTrackRef.current.offsetWidth) * totalValue
      ).toFixed(1)
    );
    await setValues({
      start,
      end,
    });
    if (commit) {
      await submitForm();
    }
  };

  // const changeLocalStartEnd = (start, end) => {
  //   setSliderPositions({
  //     ...sliderState,
  //     startValue: Number(start.toFixed(1)),
  //     endValue: Number(end.toFixed(1)),
  //   });
  // };
  useEffect(() => {
    const [x, width] = getXAndWidth(start, end, totalValue);
    const maxDuration = maxValue ?? 0;
    const minWidth =
      (sliderTrackRef.current.offsetWidth * (minValue ?? 0)) / totalValue;
    const maxWidth =
      (sliderTrackRef.current.offsetWidth * maxDuration) / totalValue;
    setSliderPositions({
      ...sliderState,
      sliderX: x,
      sliderWidth: width,
      sliderMinimumWidth: minWidth,
      sliderMaximumWidth: maxWidth,
      startValue: start,
      endValue: Math.min(start + maxDuration, end),
    });
  }, [start, end, totalValue, minValue, maxValue]);

  // useEffect(() => {
  //   if (
  //     values.start >= 0 &&
  //     values.end > 0 &&
  //     (values.start !== start || values.end !== end)
  //   )
  //     debouncedSubmit(values);
  // }, [values]);

  const onChangeStart = (val) => {
    const [x, width] = getXAndWidth(val, endValue, totalValue);
    if (minValue <= endValue - Math.max(val, 0) && val < values.end) {
      setSliderPositions({
        ...sliderState,
        sliderX: x,
        sliderWidth: width,
        startValue: val,
      });
    } else {
      setSliderPositions({
        ...sliderState,
        startValue: val,
      });
    }
  };

  const onChangeEnd = (val) => {
    const [x, width] = getXAndWidth(startValue, val, totalValue);
    if (
      minValue <= startValue - Math.min(val, Number(totalValue)) &&
      values.start < val
    ) {
      setSliderPositions({
        ...sliderState,
        sliderX: x,
        sliderWidth: width,
        endValue: val,
      });
    } else {
      setSliderPositions({
        ...sliderState,
        endValue: val,
      });
    }
  };

  return (
    <div>
      <Flex align="center">
        {showInputBoxes && (
          <Flex direction="column" mr={2}>
            {/*<StyledLabel htmlFor="range-start" className="m-0">*/}
            {/*  Start*/}
            {/*</StyledLabel>*/}
            <Input
              id="range-start"
              placeholder="0"
              onChange={async (e) => {
                // onChangeStart(Number(e.target.value));
                await handleChange(e);
                submitForm();
              }}
              name="start"
              value={values.start}
              min="0"
              max={endValue}
              w="14"
            />
          </Flex>
        )}

        <StyledTrack id="range-slider" ref={sliderTrackRef}>
          <div className="track" />
          <StyledGrabber
            bounds="#range-slider"
            default={{
              x: sliderX,
              y: 0,
              width: "100%",
              height: "100%",
            }}
            position={{ x: sliderX, y: 0 }}
            enableResizing={{
              left: true,
              right: true,
              top: false,
              bottom: false,
            }}
            size={{ width: sliderWidth, height: "100%" }}
            resizeHandleClasses={{
              left: "left",
              right: "right",
            }}
            onResize={onResize}
            onResizeStop={(e, direction, ref, delta, pos) => {
              onResize(e, direction, ref, delta, pos, true);
            }}
            onDrag={onDrag}
            onDragStop={(e, { x, node }) => {
              onDrag(e, { x, node }, true);
            }}
            minWidth={sliderMinimumWidth}
            maxWidth={sliderMaximumWidth}
          >
            <div className="bar" />
          </StyledGrabber>
        </StyledTrack>

        {showInputBoxes && (
          <Flex direction="column" ml={2}>
            {/*<StyledLabel htmlFor="range-end">End</StyledLabel>*/}
            <Input
              id="range-end"
              name="end"
              onChange={async (e) => {
                // onChangeEnd(Number(e.target.value));
                await handleChange(e);
                submitForm();
              }}
              value={values.end}
              min={startValue}
              max={totalValue}
              placeholder="0"
              w="14"
            />
          </Flex>
        )}
      </Flex>
      <div>
        {errors?.all && (
          <Text fontSize="sm" color="brand.danger">
            {errors.all} <br />
          </Text>
        )}
        {errors?.start && (
          <Text fontSize="sm" color="brand.danger">
            {errors.start} <br />
          </Text>
        )}
        {errors?.end && (
          <Text fontSize="sm" color="brand.danger">
            {errors.end} <br />
          </Text>
        )}
      </div>
    </div>
  );
};
TldrRndSlider.defaultProps = {
  startTime: null,
  endTime: null,
  totalValue: 1,
  minValue: 0,
};

TldrRndSlider.propTypes = {
  showInputBoxes: PropTypes.bool,
  startTime: PropTypes.number,
  endTime: PropTypes.number,
  totalValue: PropTypes.number.isRequired,
  minValue: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default TldrRndSlider;
